import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useFeatureAppConfig } from './use-feature-app-config';

import { createBffFetchOptions } from '../utils/create-bff-fetch-options';
import { useLogger } from '../utils/logging';
import { makeCancelable } from '../utils/make-cancelable';

import { BffFetchError, fetchBffData } from '../bff-utils/fetch-bff-data';
import { useFeatureAppEnvironment } from '../context/feature-app-env.context';

type AssetBundlesResponse = Awaited<ReturnType<typeof fetchBffData<'/asset-bundles'>>>;
type AssetBundlesStatus = 'idle' | 'loading' | 'success' | 'error';

interface UseAssetBundlesParams {
	readonly languages?: string;
}

interface AssetBundlesState {
	readonly status: AssetBundlesStatus;
	readonly data?: AssetBundlesResponse;
	readonly error?: Error;
}

interface UseAssetBundlesResult {
	readonly data?: AssetBundlesResponse;
	readonly error?: Error;
	readonly isLoading: boolean;
	readonly refetch: (params?: UseAssetBundlesParams) => void;
}

export function useAssetBundles(initialParams: UseAssetBundlesParams = {}): UseAssetBundlesResult {
	const { baseUrl, featureServices } = useFeatureAppEnvironment();
	const config = useFeatureAppConfig();
	const logger = useLogger();

	const fetchOptions = useMemo(
		() =>
			createBffFetchOptions({
				baseUrl,
				config,
				featureServices
			}),
		[featureServices, config, baseUrl]
	);

	const [queryParams, setQueryParams] = useState<UseAssetBundlesParams>({
		...initialParams
	});

	const [state, setState] = useState<AssetBundlesState>({ status: 'idle' });
	const fetchCancelRef = useRef<() => void>(() => undefined);

	const runFetch = useCallback(
		(currentParams: UseAssetBundlesParams) => {
			fetchCancelRef.current();
			setState(prev => ({ status: 'loading', data: prev.data, error: undefined }));
			const { promise, cancel } = makeCancelable(
				fetchBffData('/asset-bundles', {
					...fetchOptions,
					...currentParams
				})
			);

			fetchCancelRef.current = cancel;

			promise
				.then(result => {
					setState({ status: 'success', data: result });
				})
				.catch(error => {
					const normalizedError = error instanceof Error ? error : new Error(String(error));

					if (error instanceof BffFetchError) {
						logger.error('[useAssetBundles] BFF request failed', {
							message: error.message,
							bffLoggerCalls: error.bffLoggerCalls
						});
					} else {
						logger.error('[useAssetBundles] Unexpected error', normalizedError);
					}

					setState({
						status: 'error',
						error: normalizedError
					});
				});
		},
		[fetchOptions, logger]
	);

	useEffect(() => {
		runFetch(queryParams);

		return () => {
			fetchCancelRef.current();
		};
	}, [queryParams, runFetch]);

	const refetch = (params: UseAssetBundlesParams = {}) => {
		setQueryParams(prev => ({ ...prev, ...params }));
	};

	return {
		data: state.data,
		error: state.error,
		isLoading: state.status === 'loading',
		refetch
	};
}
